/* eslint-disable max-classes-per-file */
import { APIHandler } from "../apiHandler";
import { HTTPError } from "../httpError";

class ContentsHandler extends APIHandler {
  async get({ path }: { path: string | null }) {
    path = path || "";
    const type = this.getQueryArguement("type", "");
    if (type === null) {
      throw new HTTPError(400, `Type ${type} is invalid`);
    }
    const format = this.getQueryArguement("format", null);
    if ([null, "text", "base64"].indexOf(format) < 0) {
      throw new HTTPError(400, `Format ${format} is invalid`);
    }
    const contentString = this.getQueryArguement("content", "1");
    if (!contentString || ["0", "1"].indexOf(contentString) < 0) {
      throw new HTTPError(400, `Content ${contentString} is invalid`);
    }
    const content = parseInt(contentString, 10);
    const model = await this.contentsManager.get(path, type, format, content);
    this.finishModel(model, false);
  }

  async post({ path }: { path: string }) {
    this.setStatus(201);
    this.put({ path });
  }

  async put({ path }: { path: string }) {
    const model = await this.request.json();
    if (model) {
      if (model.copy_from) {
        throw new HTTPError(400, "Cannot copy with PUT, only POST");
      }
      const exists = await this.contentsManager.fileExists(path);
      if (exists) {
        await this.save(model, path);
      } else {
        await this.upload(model, path);
      }
    } else {
      await this.newUntitled(path);
    }
  }

  private async save(model: {}, path: string) {
    model = await this.contentsManager.save(model, path);
    this.validateModel(model, false);
    this.finishModel(model, null);
  }

  private async upload(_model: any, _path: any) {
    throw new Error("Method not implemented");
  }

  private async newUntitled(_path: any) {
    throw new Error("Method not implemented");
  }

  private finishModel(model: any, location: boolean | null) {
    location = location ?? true;
    if (location) {
      const locationUrl = this.locationUrl(model.path);
      this.setHeader("Location", locationUrl);
    }
    this.setHeader("Last-Modified", model.last_modified);
    this.setHeader("Content-Type", "application/json");
    this.finish(model);
  }

  private validateModel(model: any, expectContent: boolean) {
    // JupyterLab seems to expect the format to be null when
    // this request is sent, but JSON when received.
    // We'll just clear it when we send it to avoid errors.
    model.format = null;
    const requiredKeys = [
      "name",
      "path",
      "type",
      "writable",
      "created",
      "last_modified",
      "mimetype",
      "content",
      "format",
    ];

    const missing = requiredKeys.filter(
      key => !Object.keys(model).includes(key)
    );
    if (missing.length > 0) {
      throw new HTTPError(500, `Missing Model Keys: ${missing.join(", ")}`);
    }

    const maybeNoneKeys = ["content", "format"];
    if (expectContent) {
      const errors = maybeNoneKeys.filter(key => !model[key]);
      if (errors.length > 0) {
        throw new HTTPError(
          500,
          `Keys unexpectedly None: ${errors.join(", ")}`
        );
      }
    } else {
      const errors = maybeNoneKeys.filter(key => model[key]);
      if (errors.length > 0) {
        throw new HTTPError(
          500,
          `Keys unexpectedly not None: ${errors.join(", ")}`
        );
      }
    }
  }
}

class ContentListHandler extends APIHandler {
  async get() {
    const body = await this.contentsManager.get("", "directory", "json", 0);
    this.finish(body);
  }
}

class CheckpointsHandler extends APIHandler {
  async get() {
    this.finish([
      // TODO(herbison): This fakes that we have a checkpoint
      // JupyterLab will not load a file if there are no checkpoints.
      { id: "checkpoint", last_modified: new Date().toISOString() },
    ]);
  }

  async post() {
    this.setStatus(201);
    this.finish({ id: "checkpoint", last_modified: new Date().toISOString() });
  }
}

export const defaultHandlers: [string, typeof APIHandler][] = [
  ["/api/contents", ContentListHandler],
  ["/api/contents/:path", ContentsHandler],
  ["/api/contents/:path/checkpoints", CheckpointsHandler],
];
